import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SearchIcon from '../../assets/icons/search.svg?react';
import { InterventionRoomPropertyControllerApi } from '../../api/generated';
import type { InterventionRoomPropertyUsageResponse } from '../../api/generated';
import { createApiConfiguration } from '../../api/apiConfig';
import { useAppDispatch } from '../../store/hooks';
import { setInterventionRoomProperties } from '../../store/interventionRoomProperties/interventionRoomPropertiesSlice';
import { showError } from '../../store/errorDialog/errorDialogSlice';
import { Table, useTableSort } from '../UI';
import { SectionHeader } from '../SectionHeader/SectionHeader';
import { DeleteInterventionRoomPropertyModal } from './DeleteInterventionRoomPropertyModal';
import { FooterCell } from './FooterCell';
import { getInterventionRoomPropertiesColumns } from './interventionRoomPropertiesColumns';
import { DEFAULT_INTERVENTION_ROOM_PROPERTIES_SORT } from './interventionRoomPropertiesConstants';
import styles from './InterventionRoomPropertiesSettings.module.css';

const NEW_PROPERTY_ID = 'new' as const;
type EditingPropertyId = string | null;

export const InterventionRoomPropertiesSettings = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [properties, setProperties] = useState<InterventionRoomPropertyUsageResponse[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const { sort, handleSortChange } = useTableSort(DEFAULT_INTERVENTION_ROOM_PROPERTIES_SORT);
  const [editingPropertyId, setEditingPropertyId] = useState<EditingPropertyId>(null);
  const [editingName, setEditingName] = useState('');
  const [editingComment, setEditingComment] = useState('');
  const [propertyToDelete, setPropertyToDelete] =
    useState<InterventionRoomPropertyUsageResponse | null>(null);

  const refreshProperties = useCallback(async () => {
    try {
      const config = createApiConfiguration();
      const api = new InterventionRoomPropertyControllerApi(config);
      const response = await api.listInterventionRoomPropertiesWithUsage();
      setProperties(response.data);
      // Write through to the shared cache: the fetch-if-empty hook that fills it
      // never refetches, so consumers (the intervention builder's property
      // picker, InterventionRoomsSettings) would stay stale until a reload.
      // Mapped down to the slice's shape, which carries no usage count.
      dispatch(
        setInterventionRoomProperties(
          response.data.map(({ id, name, description }) => ({ id, name, description }))
        )
      );
    } catch (error) {
      console.error('Failed to fetch intervention room properties:', error);
      dispatch(showError({ message: t('fetch-error') }));
    }
  }, [dispatch, t]);

  useEffect(() => {
    const init = async () => {
      await refreshProperties();
    };
    void init();
  }, [refreshProperties]);

  const filteredProperties = useMemo(() => {
    if (!searchQuery) return properties;
    const q = searchQuery.toLowerCase();
    return properties.filter((p) => p.name.toLowerCase().includes(q));
  }, [properties, searchQuery]);

  const effectiveEditingPropertyId: EditingPropertyId =
    editingPropertyId === null || editingPropertyId === NEW_PROPERTY_ID
      ? editingPropertyId
      : filteredProperties.some((p) => p.id === editingPropertyId)
        ? editingPropertyId
        : null;

  const startEdit = useCallback((property: InterventionRoomPropertyUsageResponse) => {
    setEditingPropertyId(property.id);
    setEditingName(property.name);
    setEditingComment(property.description ?? '');
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingPropertyId(null);
    setEditingName('');
    setEditingComment('');
  }, []);

  const saveEdit = useCallback(async () => {
    if (!effectiveEditingPropertyId || effectiveEditingPropertyId === NEW_PROPERTY_ID) return;
    const trimmedName = editingName.trim();
    if (trimmedName.length === 0) return;
    const propertyId = effectiveEditingPropertyId;
    try {
      const config = createApiConfiguration();
      const api = new InterventionRoomPropertyControllerApi(config);
      await api.updateInterventionRoomProperty({
        propertyId,
        updateInterventionRoomPropertyRequest: {
          name: trimmedName,
          description: editingComment || undefined
        }
      });
      setEditingPropertyId(null);
      setEditingName('');
      setEditingComment('');
      await refreshProperties();
    } catch (error) {
      console.error('Failed to update intervention room property:', error);
      dispatch(showError({ message: t('update-error') }));
    }
  }, [effectiveEditingPropertyId, editingName, editingComment, refreshProperties, dispatch, t]);

  const createProperty = useCallback(
    async (name: string) => {
      try {
        const config = createApiConfiguration();
        const api = new InterventionRoomPropertyControllerApi(config);
        await api.createInterventionRoomProperty({
          createInterventionRoomPropertyRequest: { name }
        });
        setEditingPropertyId(null);
        await refreshProperties();
      } catch (error) {
        console.error('Failed to create intervention room property:', error);
        dispatch(showError({ message: t('update-error') }));
      }
    },
    [refreshProperties, dispatch, t]
  );

  const handleDeleted = useCallback(() => {
    setPropertyToDelete(null);
    void refreshProperties();
  }, [refreshProperties]);

  const columns = useMemo(
    () =>
      getInterventionRoomPropertiesColumns(t, {
        editingPropertyId: effectiveEditingPropertyId,
        editingName,
        editingComment,
        onStartEdit: startEdit,
        onNameChange: setEditingName,
        onCommentChange: setEditingComment,
        onSave: () => {
          void saveEdit();
        },
        onCancel: cancelEdit,
        onDelete: (property) => setPropertyToDelete(property)
      }),
    [t, effectiveEditingPropertyId, editingName, editingComment, startEdit, saveEdit, cancelEdit]
  );

  return (
    <section>
      <SectionHeader title={t('settings-intervention-room-properties-header')}>
        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>
              {t('settings-search-intervention-room-property')}
            </label>
            <div className={styles.searchInputWrapper}>
              <SearchIcon className={styles.searchIcon} />
              <input
                type="text"
                className={styles.searchInput}
                value={searchQuery}
                placeholder={t('intervention-room-properties-search-placeholder')}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </SectionHeader>

      <Table
        columns={columns}
        data={filteredProperties}
        sort={sort}
        onSortChange={handleSortChange}
        getRowKey={(property) => property.id}
        footer={
          <FooterCell
            isEditing={editingPropertyId === NEW_PROPERTY_ID}
            onStartEdit={() => {
              setEditingPropertyId(NEW_PROPERTY_ID);
              setEditingName('');
              setEditingComment('');
            }}
            onSave={(name) => void createProperty(name)}
            onCancel={cancelEdit}
          />
        }
      />

      {propertyToDelete && (
        <DeleteInterventionRoomPropertyModal
          property={propertyToDelete}
          onClose={() => setPropertyToDelete(null)}
          onDeleted={handleDeleted}
        />
      )}
    </section>
  );
};
